/**
 * The BrightFlare logo, as the two marks the brand actually ships:
 *
 *   - `<BrandMark>`     — the star ("Stern"), used where a square-ish mark fits:
 *                         the header lock-up next to the product name.
 *   - `<BrandWordmark>` — the "BrightFlare" lettering, used where the company
 *                         itself is named: the login title image.
 *
 * Both files are the originals from brightflare.io, self-hosted under
 * `public/brand/` (no CDN call at runtime), and both exist only in black and
 * white. There is therefore no CSS filter trick here: the component renders BOTH
 * files and the stylesheet shows the one matching `prefers-color-scheme`
 * (`.theme-light-only` / `.theme-dark-only` in global.css). `tone` overrides that
 * when the background is fixed regardless of the scheme — the login title image
 * is always dark, so it always wants the white lettering.
 *
 * Brand Guidelines (CI März 2026, section 2) the call sites must keep:
 *   - black on light, white on dark — never recoloured, faded (`opacity`),
 *     shadowed or rotated;
 *   - the wordmark is never smaller than 70px high — where that does not fit,
 *     name the company in plain text instead of shrinking the logo;
 *   - the "B" logo icon and the wordmark never share a page; the star alone
 *     may accompany either. (The favicon is the AssetRadar icon, not the "B".)
 *
 * The marks are decorative wherever a text label sits right next to them, which
 * is every current call site; `alt` therefore defaults to '' and the images stay
 * out of the accessibility tree. Pass `alt` to opt back in.
 */

import type { ReactNode } from 'react';

/** 'auto' follows the colour scheme; 'light'/'dark' name the BACKGROUND. */
export type LogoTone = 'auto' | 'light' | 'dark';

interface LogoProps {
  /** Background the logo sits on. Default 'auto' — follow the colour scheme. */
  tone?: LogoTone;
  /** Extra class on every rendered <img> (the height is set in CSS). */
  className?: string;
  /** Non-empty makes the logo meaningful content instead of decoration. */
  alt?: string;
}

const BLACK_STAR = '/brand/brightflare-star-black.svg';
const WHITE_STAR = '/brand/brightflare-star-white.svg';
const BLACK_WORDMARK = '/brand/brightflare-wordmark-black.svg';
const WHITE_WORDMARK = '/brand/brightflare-wordmark-white.svg';

/**
 * Renders one <img> for a fixed tone, or both variants (scheme-switched in CSS)
 * for `tone === 'auto'`.
 */
function Logo({
  onLight,
  onDark,
  base,
  tone = 'auto',
  className,
  alt = '',
}: LogoProps & { onLight: string; onDark: string; base: string }): ReactNode {
  const cls = className === undefined ? base : `${base} ${className}`;
  // A dark background needs the white file, and vice versa.
  if (tone === 'dark') return <img className={cls} src={onDark} alt={alt} />;
  if (tone === 'light') return <img className={cls} src={onLight} alt={alt} />;
  return (
    <>
      <img className={`${cls} theme-light-only`} src={onLight} alt={alt} />
      <img className={`${cls} theme-dark-only`} src={onDark} alt={alt} aria-hidden={alt === ''} />
    </>
  );
}

/** The BrightFlare star. */
export function BrandMark(props: LogoProps): ReactNode {
  return <Logo {...props} base="bf-mark" onLight={BLACK_STAR} onDark={WHITE_STAR} />;
}

/** The BrightFlare wordmark. */
export function BrandWordmark(props: LogoProps): ReactNode {
  return <Logo {...props} base="bf-wordmark" onLight={BLACK_WORDMARK} onDark={WHITE_WORDMARK} />;
}

/*
 * The AssetRadar product marks, from the AssetRadar Logo Guide (in the
 * BrightFlare design system). Rules the call sites must keep:
 *   - icon and wordmark are one fixed unit — never re-set "AssetRadar" in text
 *     next to the icon, never recolour, distort or add effects;
 *   - "positiv" on light backgrounds, "negativ" on dark ones; the icon likewise
 *     "dunkel" on light and "hell" on dark;
 *   - minimum width: logo 120px, icon 24px; clear space ¼ of the icon height.
 */
const AR_LOGO_POSITIVE = '/brand/assetradar-logo-positiv.svg';
const AR_LOGO_NEGATIVE = '/brand/assetradar-logo-negativ.svg';
const AR_ICON_DARK = '/brand/assetradar-icon-dunkel.svg';
const AR_ICON_LIGHT = '/brand/assetradar-icon-hell.svg';

/** The AssetRadar logo (icon + wordmark). */
export function ProductLogo(props: LogoProps): ReactNode {
  return <Logo {...props} base="ar-logo" onLight={AR_LOGO_POSITIVE} onDark={AR_LOGO_NEGATIVE} />;
}

/** The AssetRadar icon alone, for places too narrow for the 120px logo. */
export function ProductIcon(props: LogoProps): ReactNode {
  return <Logo {...props} base="ar-icon" onLight={AR_ICON_DARK} onDark={AR_ICON_LIGHT} />;
}
